import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AuthProvider from './contexts/AuthProvider';
import PrivateRoute from './components/PrivateRoute';
import AppShell from './app/AppShell';
import { ToastProvider } from './components/ui/Toast';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import Dashboard from './pages/Dashboard';
import MyWork from './pages/MyWork';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Brands from './pages/Brands';
import BrandDetail from './pages/BrandDetail';
import BrandVisibility from './pages/BrandVisibility';
import Social from './pages/Social';
import Newsletters from './pages/Newsletters';
import Homepage from './pages/Homepage';
import ShopBanners from './pages/ShopBanners';
import ShopBannerEditor from './pages/ShopBannerEditor';
import Tasks from './pages/Tasks';
import Notifications from './pages/Notifications';
import CampaignPartnership from './pages/CampaignPartnership';
import CampaignPartnershipEditor from './pages/CampaignPartnershipEditor';
import BrandResults from './pages/BrandResults';
// Lazy-load the lookbooks so @react-pdf/renderer (~250kb) stays out of
// the main bundle and only ships when the user actually opens one.
const SharedCampaign = lazy(() => import('./pages/SharedCampaign'));
const CampaignLookbook = lazy(() => import('./pages/CampaignLookbook'));
const BrandLookbook = lazy(() => import('./pages/BrandLookbook'));
import HowToGuide from './pages/HowToGuide';
import BrandKit from './pages/BrandKit';
import RateCard from './pages/RateCard';
import BFRegister, { BFRedirect } from './pages/blackFriday/BFRegister';
import BFBrandTerms from './pages/blackFriday/BFBrandTerms';
import BFHistory from './pages/blackFriday/BFHistory';
import BFCalendar, { BFCalendarRedirect } from './pages/blackFriday/BFCalendar';
import BrandDealPage from './pages/BrandDeal';
const PublicDeal = lazy(() => import('./pages/PublicDeal'));
import PublicBrandKit from './pages/PublicBrandKit';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const protect = (node: React.ReactNode) => (
  <PrivateRoute>
    <AppShell>{node}</AppShell>
  </PrivateRoute>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/sso-callback" element={<SSOCallback />} />
              <Route path="/partnerships/:slug" element={<CampaignPartnership />} />
              <Route path="/share/:token" element={<SharedCampaign />} />
              <Route path="/share/brand/:token" element={<SharedCampaign kind="brand" />} />
              <Route path="/results/:slug" element={<BrandResults />} />
              <Route path="/deal/:token" element={<PublicDeal />} />
              <Route path="/brand-kit/share" element={<PublicBrandKit />} />
              <Route path="/" element={protect(<Dashboard />)} />
              <Route path="/my-work" element={protect(<MyWork />)} />
              <Route path="/campaigns" element={protect(<Campaigns />)} />
              <Route path="/campaigns/:id" element={protect(<CampaignDetail />)} />
              <Route path="/campaigns/:id/partnership" element={protect(<CampaignPartnershipEditor />)} />
              <Route
                path="/campaigns/:id/lookbook"
                element={protect(
                  <Suspense fallback={<div style={{ padding: 32, color: 'var(--ink-4)' }}>Loading lookbook…</div>}>
                    <CampaignLookbook />
                  </Suspense>
                )}
              />
              <Route path="/brands" element={protect(<Brands />)} />
              <Route path="/brands/:id" element={protect(<BrandDetail />)} />
              <Route path="/brands/:id/visibility" element={protect(<BrandVisibility />)} />
              <Route path="/brands/:id/deal/:year" element={protect(<BrandDealPage />)} />
              <Route
                path="/brands/:id/lookbook"
                element={protect(
                  <Suspense fallback={<div style={{ padding: 32, color: 'var(--ink-4)' }}>Loading lookbook…</div>}>
                    <BrandLookbook />
                  </Suspense>
                )}
              />
              <Route path="/social" element={protect(<Social />)} />
              <Route path="/newsletters" element={protect(<Newsletters />)} />
              <Route path="/homepage" element={protect(<Homepage />)} />
              <Route path="/shop-banners" element={protect(<ShopBanners />)} />
              <Route path="/shop-banners/recurring/:recurringId" element={protect(<ShopBannerEditor />)} />
              <Route path="/shop-banners/:assetId" element={protect(<ShopBannerEditor />)} />
              <Route path="/tasks" element={protect(<Tasks />)} />
              <Route path="/notifications" element={protect(<Notifications />)} />
              <Route path="/how-to" element={protect(<HowToGuide />)} />
              <Route path="/brand-kit" element={protect(<BrandKit />)} />
              <Route path="/rate-card" element={protect(<RateCard />)} />
              <Route path="/black-friday" element={protect(<BFRedirect />)} />
              <Route path="/black-friday/history" element={protect(<BFHistory />)} />
              <Route path="/black-friday/calendar" element={protect(<BFCalendarRedirect />)} />
              <Route path="/black-friday/calendar/:year" element={protect(<BFCalendar />)} />
              <Route path="/black-friday/:year" element={protect(<BFRegister />)} />
              <Route path="/black-friday/:year/:brandId" element={protect(<BFBrandTerms />)} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
